import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import classNames from 'classnames';
import { format, isValid, parse } from 'date-fns';

import CollapsibleCard from 'core/components/collapsible-card';
import { Button } from 'core/components/button';
import { Label } from 'core/components/label';
import { Table } from 'core/components/table';
import { Tag } from 'core/components/tag';
import useStyles from './diff-summary.styles';

const CHANGE_TAG_LABELS = { added: 'Added', removed: 'Removed' };

// Rows arrive sorted by section then item, so consecutive rows with the same section + item form a group.
const groupRows = (rows) =>
  rows.reduce((groups, row) => {
    const title = row.itemLabel ?? row.section;
    const lastGroup = groups[groups.length - 1];
    if (lastGroup?.title === title) {
      lastGroup.rows.push(row);
    } else {
      groups.push({ title, rows: [row] });
    }
    return groups;
  }, []);

const Value = ({ field, value }) => {
  const classes = useStyles();
  if (value === null) {
    return <span className={classes.missingValue}>(not present)</span>;
  }
  if (value === '') {
    return <span className={classes.missingValue}>(empty)</span>;
  }
  if (field.endsWith('YN') && (value === '0' || value === '1')) {
    return value === '1' ? 'Yes' : 'No';
  }
  if (/(?:^date_|_date$|(?:Date|Dt)$)/.test(field) && /^\d{8}$/.test(value)) {
    const date = parse(value, 'yyyyMMdd', new Date(0));
    if (isValid(date)) {
      return format(date, 'MMM d, yyyy');
    }
  }
  return value;
};

const GroupHeading = ({ title }) => {
  const classes = useStyles();
  // The builder's driver/vehicle labels have a fixed structure; separate the description from the identity.
  const [, position, description, systemId] =
    title.match(/^((?:Driver|Vehicle) \d+)(?:: (.+?))?(?: \(SystemId ([^)]+)\))?$/) ?? [];
  const metadata = [description && position, systemId && `SystemId ${systemId}`].filter(Boolean).join(' · ');
  return (
    <div className={classes.groupHeading}>
      <Label type="infoSubtitle" className={classes.groupTitle}>
        {description ?? position ?? title}
      </Label>
      {metadata && (
        <Label type="infoValue" className={classes.groupMetadata}>
          {metadata}
        </Label>
      )}
    </div>
  );
};

const DiffTable = ({ rows }) => {
  const classes = useStyles();
  return (
    <Table
      selfContained={false}
      header={
        <>
          <TableCell className={classes.fieldColumn}>Field</TableCell>
          <TableCell className={classes.valueColumn}>Before</TableCell>
          <TableCell className={classes.valueColumn}>After</TableCell>
        </>
      }
      body={rows.map(({ field, before, after, change }) => (
        <TableRow key={field}>
          <TableCell className={classes.fieldColumn}>
            <div className={classes.fieldCell}>
              <span className={classes.fieldName}>{field}</span>
              {CHANGE_TAG_LABELS[change] && (
                <Tag className={classNames(classes.changeTag, classes[change])}>{CHANGE_TAG_LABELS[change]}</Tag>
              )}
            </div>
          </TableCell>
          <TableCell className={classNames(classes.valueCell, { [classes.beforeValue]: before !== null })}>
            <Value field={field} value={before} />
          </TableCell>
          <TableCell className={classNames(classes.valueCell, { [classes.afterValue]: after !== null })}>
            <Value field={field} value={after} />
          </TableCell>
        </TableRow>
      ))}
    />
  );
};

const changeCount = (rows) => `${rows.length} ${rows.length === 1 ? 'change' : 'changes'}`;
const COMPUTED_TITLE = 'Computed by ClarionDoor';

const DiffSummary = ({ rows, header, emptyMessage }) => {
  const classes = useStyles();
  const [openGroups, setOpenGroups] = useState({});
  const groups = useMemo(() => {
    const inputGroups = groupRows(rows.filter((row) => !row.computed));
    const computedRows = rows.filter((row) => row.computed);
    return computedRows.length
      ? [...inputGroups, { title: COMPUTED_TITLE, rows: computedRows, computed: true }]
      : inputGroups;
  }, [rows]);

  if (!groups.length) {
    return (
      <>
        {header}
        <Label>{emptyMessage}</Label>
      </>
    );
  }

  const isOpen = (group) => openGroups[group.title] ?? !group.computed;
  const setAllOpen = (open) => setOpenGroups(Object.fromEntries(groups.map(({ title }) => [title, open])));

  return (
    <>
      <div className={classes.toolbar}>
        {header}
        <div className={classes.controls}>
          <Button
            className={classes.controlButton}
            variant="text"
            color="primary"
            disabled={groups.every(isOpen)}
            onClick={() => setAllOpen(true)}
          >
            Expand all
          </Button>
          <Button
            className={classes.controlButton}
            variant="text"
            color="primary"
            disabled={groups.every((group) => !isOpen(group))}
            onClick={() => setAllOpen(false)}
          >
            Collapse all
          </Button>
        </div>
      </div>
      {groups.map((group) => (
        <CollapsibleCard
          key={group.title}
          open={isOpen(group)}
          onChange={(open) => setOpenGroups((previous) => ({ ...previous, [group.title]: open }))}
          className={classes.group}
          classes={{
            content: classes.cardContent,
            contentRight: classes.cardContentRight,
            buttonContainer: classes.cardButtons
          }}
          content={<GroupHeading title={group.title} />}
          contentRight={<Tag className={classes.changeCount}>{changeCount(group.rows)}</Tag>}
        >
          {group.computed ? (
            groupRows(group.rows).map(({ title, rows }) => (
              <div key={title}>
                <Label type="infoSubtitle" className={classes.computedSection}>
                  {title}
                </Label>
                <DiffTable rows={rows} />
              </div>
            ))
          ) : (
            <DiffTable rows={group.rows} />
          )}
        </CollapsibleCard>
      ))}
    </>
  );
};

DiffSummary.propTypes = {
  rows: PropTypes.array.isRequired,
  header: PropTypes.node,
  emptyMessage: PropTypes.string.isRequired
};

export default DiffSummary;
